import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PlayCircle, TrendingUp, Clock, Trophy, Wand2, Home, Droplets, Bath, Layers, Wind, Grip, ArrowRightLeft, ChefHat } from "lucide-react";
import { Header } from "@/components/Header";
import { PublicFooter } from "@/components/PublicFooter";
import { SEO, SITE_URL } from "@/components/SEO";
import { api } from "@/lib/api";

const track = (event) => { api.post("/track", { event }).catch(() => {}); };

// Auto-playing before→after showcase (custom, non-interactive; the wrapper links to /demo).
function HeroSlider() {
  const [pos, setPos] = useState(60);
  useEffect(() => {
    let raf, start;
    const loop = (t) => {
      if (start === undefined) start = t;
      const e = (t - start) / 1000;
      setPos(50 + 32 * Math.sin((e * 2 * Math.PI) / 7)); // gentle 7s sweep, ~18%..82%
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <div className="absolute inset-0 select-none" data-testid="hero-autoslider">
      <img src="/demo-after.jpg" alt="Kitchen after reface" className="absolute inset-0 h-full w-full object-cover" draggable={false} />
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src="/demo-before.jpg" alt="Kitchen before reface" className="absolute inset-0 h-full w-full object-cover" draggable={false} />
      </div>
      <div className="absolute inset-y-0 w-[3px] bg-white/90 shadow-[0_0_8px_rgba(0,0,0,0.35)]" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 left-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md">
          <ArrowRightLeft className="h-4 w-4 text-[var(--ink)]" strokeWidth={2} />
        </div>
      </div>
    </div>
  );
}

const HOME_JSONLD = [
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Customize Studio",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: SITE_URL,
    description:
      "AI before & after visualization software for home improvement sales teams. Render photorealistic finished-project previews from a single photo.",
    offers: { "@type": "Offer", price: "149.99", priceCurrency: "USD" },
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Customize Studio",
    url: SITE_URL,
    logo: `${SITE_URL}/logo512.png`,
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      ["What is Customize Studio?", "Customize Studio is AI visualization software that turns a single photo into a photorealistic before-and-after preview of a finished home improvement project in seconds."],
      ["Do I need design or CAD skills?", "No. You upload a photo, pick the products you sell, and the AI renders the finished result automatically."],
      ["Which trades does it support?", "All product-based home improvement trades — kitchens, bathrooms, roofing, siding, windows, gutters, showers and more — because each company builds its own catalog."],
      ["How much does it cost?", "Plans start at $149.99/month including 1,000 render credits. Each render uses 10 credits, and top-up credit packs are available."],
    ].map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
  },
];

const TRADES = [
  { icon: Home, label: "Roofing" },
  { icon: Droplets, label: "Showers" },
  { icon: Bath, label: "Bathrooms" },
  { icon: ChefHat, label: "Kitchens" },
  { icon: Layers, label: "Siding" },
  { icon: ArrowRightLeft, label: "Gutters" },
  { icon: Grip, label: "Windows" },
  { icon: Wind, label: "Doors" },
];

export default function Landing() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Customize Studio — AI Before & After for Home Improvement Sales"
        description="AI visualization software that turns a photo into a photorealistic before-and-after of the finished project in seconds. Built for home improvement sales teams."
        canonical={SITE_URL + "/"}
        jsonLd={HOME_JSONLD}
      />
      <Header />
      <section className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 py-16 md:px-10 lg:grid-cols-[1.1fr_1fr] lg:py-24">
        <div>
          <p className="overline">For home improvement remodelers</p>
          <div className="mt-3 text-center">
            <h1 className="font-display text-[1.75rem] font-semibold leading-[1.1] tracking-tight text-black sm:text-4xl lg:text-[3.25rem]">
              Turn their dream into{" "}
              <span className="bp-word">
                {[
                  { c: "v", d: "0.0s", u: "2.6s" },
                  { c: "i", d: "1.4s", u: "3.3s" },
                  { c: "r", d: "0.6s", u: "2.9s" },
                  { c: "t", d: "2.2s", u: "3.6s" },
                  { c: "u", d: "0.9s", u: "2.4s" },
                  { c: "a", d: "1.8s", u: "3.1s" },
                  { c: "l", d: "0.3s", u: "2.8s" },
                ].map((ch, i) => (
                  <span key={i} className="bp-char" style={{ animationDelay: ch.d, animationDuration: ch.u }}>{ch.c}</span>
                ))}
              </span>{" "}
              reality
            </h1>
          </div>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-[var(--ink-soft)]">
            Customize Studio turns a quick phone photo into a stunning, photorealistic preview of the finished
            project in seconds — right on the customer's doorstep. When homeowners can actually see the beautiful
            result, saying yes feels easy. Perfect for roofing, showers, bathrooms, kitchens, siding, gutters,
            windows and doors.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/signup" data-testid="get-started-btn" onClick={() => track("start_closing")}
              className="flex items-center gap-2 rounded-full bg-[var(--brand)] px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-200 ease-out hover:-translate-y-px hover:bg-[var(--brand-dark)]">
              Get Started <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </Link>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {[
              { icon: TrendingUp, t: "Increase sales", d: "Visual buyers commit faster and upsell more scope" },
              { icon: Clock, t: "Recover quotes", d: "Re-engage stalled leads with a render they can't ignore" },
              { icon: Trophy, t: "Beat competitors", d: "Offer instant designs while they wait on drawings" },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t}>
                <Icon className="h-5 w-5 text-[var(--brand)]" strokeWidth={1.5} />
                <p className="mt-2 text-sm font-semibold">{t}</p>
                <p className="text-xs text-[var(--ink-soft)]">{d}</p>
              </div>
            ))}
          </div>
        </div>
        <Link to="/demo" data-testid="hero-showcase-link" onClick={() => track("see_demo")}
          className="group relative block aspect-[1264/848] overflow-hidden rounded-2xl border border-[var(--line)] bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg">
          <HeroSlider />
          <div className="pointer-events-none absolute inset-0 flex items-start justify-center bg-black/10 pt-6 transition-all duration-200 group-hover:bg-black/25">
            <span data-testid="hero-see-in-action-btn"
              className="flex items-center gap-2 rounded-full bg-[var(--brand)] px-6 py-3 text-sm font-semibold text-white shadow-lg transition-transform duration-200 ease-out group-hover:scale-105">
              <PlayCircle className="h-4 w-4" strokeWidth={2} /> See it in action
            </span>
          </div>
          <div className="pointer-events-none absolute bottom-4 left-4 flex items-center gap-2 rounded-full bg-white/85 px-4 py-2 text-xs font-medium backdrop-blur">
            <Wand2 className="h-3.5 w-3.5 text-[var(--brand)]" /> Real result from Customize Studio
          </div>
        </Link>
      </section>

      {/* Trades band */}
      <section data-testid="trades-band" className="border-y border-[var(--line)] bg-[var(--surface-2)]">
        <div className="mx-auto max-w-7xl px-6 py-10 md:px-10">
          <div className="flex flex-wrap gap-3">
            {TRADES.map(({ icon: Icon, label }) => (
              <span key={label} data-testid={`trade-${label.toLowerCase()}`}
                className="flex items-center gap-2 rounded-full border border-[var(--line)] bg-white px-4 py-2 text-sm font-medium">
                <Icon className="h-4 w-4 text-[var(--brand)]" strokeWidth={1.5} /> {label}
              </span>
            ))}
            <span className="flex items-center gap-2 rounded-full border border-dashed border-[var(--line)] bg-white px-4 py-2 text-sm font-medium text-[var(--ink-soft)]">
              &amp; more
            </span>
          </div>
        </div>
      </section>

      {/* SEO content */}
      <section data-testid="seo-content" className="mx-auto max-w-4xl px-6 py-16 md:px-10 lg:py-20">
        <h2 className="font-display text-3xl tracking-tight md:text-4xl">
          AI before &amp; after visualization for home improvement sales
        </h2>
        <div className="mt-6 space-y-5 text-base leading-relaxed text-[var(--ink-soft)]">
          <p>
            Customize Studio is an AI-powered sales visualization tool made for product-based home improvement
            companies. Whether you sell <strong className="text-[var(--ink)]">roofing</strong>, walk-in{" "}
            <strong className="text-[var(--ink)]">showers</strong>, full{" "}
            <strong className="text-[var(--ink)]">bathroom</strong> remodels,{" "}
            <strong className="text-[var(--ink)]">siding</strong>,{" "}
            <strong className="text-[var(--ink)]">gutters</strong>,{" "}
            <strong className="text-[var(--ink)]">windows</strong> or doors, your reps can snap a photo of the
            customer's home and instantly render a photorealistic preview of the finished project.
          </p>
          <p>
            Homeowners buy what they can see. By showing a realistic before-and-after right in the living room or on
            the driveway, your team removes doubt, justifies premium materials, and closes more jobs on the first
            appointment — no CAD software, no waiting on a designer, and no expensive renderings.
          </p>
          <p>
            Manage your own product catalog of colors, finishes, styles and materials for every trade you offer, give
            each rep their own seat, and track exactly how many designs your team creates. Customize Studio scales
            with roofing crews, bath remodelers, siding installers, window companies and multi-trade home improvement
            businesses alike.
          </p>
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/signup" data-testid="seo-cta-signup" onClick={() => track("start_closing")}
            className="flex items-center gap-2 rounded-full bg-[var(--brand)] px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-200 ease-out hover:-translate-y-px hover:bg-[var(--brand-dark)]">
            Get Started <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
